<template>
  <!-- prettier-ignore -->
  <form
    ref="formRef"
    v-form-tracker="{triggerCancel, triggerInteraction}"
    novalidate
    v-bind="$attrs"
    :name="name"
    @submit.prevent="debouncedSubmit"
  >
    <slot></slot>
  </form>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { cloneDeep, debounce } from 'lodash'

import FormTracker from '@/directives/FormTracker'

import events$ from '@/services/Events'
import { useValidation } from '@/hooks'

import { EVENT_FORM } from '@/config/events'
import { DEFAULT_FORM_DEBOUNCE_TIME } from '@/config/constants'

const { pendingValidate } = useValidation()

const VFormTracker = FormTracker

const $emit = defineEmits(['submit', 'cancel'])

// Props
const props = defineProps({
  name: {
    type: String,
    required: true,
  },

  // trigger submit, even if form is not dirty
  noChangeDetection: {
    type: Boolean,
    default: false,
  },

  trackingDisabled: {
    type: Boolean,
    default: false,
  },

  validator: {
    type: Object,
    required: true,
  },
})

// Data
const formRef = ref(undefined)
defineExpose({ submit })

// Computed
const payload = computed(() => {
  return {
    form: props.name,
    errors: yieldErrors(),
  }
})

// Lifecycle Hooks
onMounted(() => {
  if (!props.trackingDisabled) events$.emit(EVENT_FORM.INIT, payload.value)
})

// Methods
const debouncedSubmit = debounce(submit, DEFAULT_FORM_DEBOUNCE_TIME)

async function handleAction() {
  const isDirty = cloneDeep(props.validator.$anyDirty)

  props.validator.$touch()

  const isValid = await props.validator.$validate()
  if (isValid) {
    // if dirty (or change detection has been disabled). trigger submit
    if (props.noChangeDetection || isDirty) {
      triggerSubmit(isDirty)
    } else {
      triggerCancel(isDirty)
    }
  } else {
    await scrollToError()
    triggerError(false)
  }
}

function submit() {
  pendingValidate(props.validator).then(handleAction).catch(handleAction)
}

function triggerCancel(dirty) {
  if (!props.trackingDisabled) {
    events$.emit(EVENT_FORM.CANCELED, payload.value)
  }

  props.validator.$reset()
  $emit('cancel', { dirty })
}

function triggerError(touch = true) {
  if (touch) props.validator.$touch()
  if (!props.trackingDisabled) events$.emit(EVENT_FORM.ERROR, payload.value)
}

function triggerInteraction(fieldName) {
  if (!props.trackingDisabled) {
    events$.emit(EVENT_FORM.INTERACTED, {
      ...payload.value,
      fieldName,
    })
  }
}

async function scrollToError() {
  await nextTick()

  const element = formRef.value.querySelector('.input--invalid, .checkbox--invalid, .group--invalid')
  const offset = element?.offsetTop
  if (element && offset > 0) {
    window.scrollTo({
      top: element.offsetTop - 50,
      behavior: 'smooth',
    })
  }
}

function triggerSubmit(dirty) {
  if (!props.trackingDisabled) events$.emit(EVENT_FORM.SUBMITTED, payload.value)

  $emit('submit', { dirty })
}

function yieldErrors() {
  if (props.validator.$errors.length === 0) return ''

  return `${props.validator.$errors
    .map(err => {
      return `${err.$property}:${err.$validator}`
    })
    .join(',')};`
}
</script>
